import { FilesOptionButton } from "common/components/DropdownMenu/FilesOptionButton";
import { OptionButton } from "common/components/DropdownMenu/OptionButton";
import { Tooltip } from "common/components/Tooltip";
import { FileWithPreview, Nullable } from "common/types";
import React, { useState, useRef, useEffect, ReactNode, MouseEvent as ReactMouseEvent } from "react";

export type DropdownOption = {
  label: ReactNode; // Can be a string or a React component
  leadingIcon?: string; // Icon to be displayed before the label
  active?: boolean;
  onClick?: () => void; // Function to be called on click
  onFileChange?: (files: FileWithPreview[]) => void; // Function to be called on click
};

interface DropdownMenuProps {
  children: ReactNode; // The element that triggers the dropdown
  options: DropdownOption[]; // Array of dropdown options
  tooltipTitle?: Nullable<string>; // Tooltip title
  optionToolTip?: boolean; // Tooltip on options
  onOpen?: () => void; // Function to be called on open
  onClose?: () => void; // Function to be called on close
  openUpwards?: boolean; // Control dropdown direction
}

export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  children,
  options,
  tooltipTitle,
  optionToolTip,
  onOpen,
  onClose,
  openUpwards = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const childrenRef = useRef<HTMLDivElement>(null);

  const setIsOpenWithCallback = (value: boolean) => {
    setIsOpen(value);
    if (value && onOpen) onOpen();
    if (!value && onClose) onClose();
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpenWithCallback(false);
      if (onClose) onClose();
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleOptionClick = (e: ReactMouseEvent<HTMLElement>, option: DropdownOption) => {
    e.stopPropagation();
    if (option.onClick) {
      option.onClick();
    }
    setIsOpenWithCallback(false);
  };

  const handleFileOptionClick = (files: FileWithPreview[], option: DropdownOption) => {
    if (option.onFileChange) {
      option.onFileChange(files);
    }
    setIsOpenWithCallback(false);
  };

  const ClickableElement = () => (
    <div
      ref={childrenRef}
      onClick={(event) => {
        setIsOpenWithCallback(!isOpen);
        event.stopPropagation();
      }}>
      {children}
    </div>
  );

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      {tooltipTitle && !isOpen ? (
        <Tooltip title={tooltipTitle} variant="bottomArrow" size="md" description="">
          <ClickableElement />
        </Tooltip>
      ) : (
        <ClickableElement />
      )}

      {isOpen && (
        <div
          className={`absolute right-0 bg-white rounded shadow-lg p-xs z-[9999] max-w-[200px] lg:max-w-[400px] xl:max-w-[600px] ${
            openUpwards ? "bottom-full mb-sm" : "mt-sm"
          }`}>
          {options.map((option, index) => {
            const ToolTipWrapper = ({ children }: { children: React.ReactNode }) =>
              optionToolTip ? (
                <Tooltip description={option.label} size="md" variant="leftArrow">
                  {children}
                </Tooltip>
              ) : (
                <>{children}</>
              );
            return (
              <React.Fragment key={index}>
                {option.onFileChange ? (
                  <FilesOptionButton index={index} option={option} onFileChange={handleFileOptionClick} />
                ) : (
                  <ToolTipWrapper>
                    <OptionButton index={index} option={option} onClick={handleOptionClick} />
                  </ToolTipWrapper>
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
};
